import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  MapPin,
  Mail,
  Clock,
  Send,
  Loader2,
} from "lucide-react";
import { BUSINESS, SERVICES } from "@/lib/business";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUIRED_FIELDS = ["name", "phone", "vehicle", "service"];
const OPTIONAL_EMPTY_FIELDS = ["email", "preferred_date", "message"];

function buildPayload(form) {
  const payload = { ...form };
  for (const k of OPTIONAL_EMPTY_FIELDS) {
    if (!payload[k]) delete payload[k];
  }
  return payload;
}

function validate(form) {
  return REQUIRED_FIELDS.every((k) => form[k]);
}

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  vehicle: "",
  service: "",
  preferred_date: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!validate(form)) {
      toast.error("Please complete the required fields.", {
        description: "Name, phone, vehicle, and service are required.",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, buildPayload(form));
      toast.success("Appointment request sent!", {
        description: "We'll call you shortly to confirm.",
      });
      setForm(EMPTY_FORM);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let description = "Please try again or call us directly.";
      if (Array.isArray(detail)) {
        const first = detail[0];
        if (first?.loc && first?.msg) {
          const field = first.loc[first.loc.length - 1];
          description = `${field}: ${first.msg}`;
        }
      } else if (typeof detail === "string") {
        description = detail;
      }
      toast.error("Couldn't send request", { description });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-28 bg-[#0B132B] text-white overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#D32F2F]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D32F2F]" />
            <span className="font-mono-label text-[#D32F2F]">Get in Touch</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.02]">
            Get Back on the Road{" "}
            <span className="text-[#D32F2F]">Today.</span>
          </h2>
          <p className="mt-5 text-white/75 text-lg leading-relaxed">
            Book your appointment online or call us directly. We'll confirm
            your slot and get you a written estimate — no pressure, no gimmicks.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          {/* Contact details */}
          <aside className="lg:col-span-2 space-y-6">
            <ContactBlock
              icon={Phone}
              label="Phone"
              value={BUSINESS.phoneDisplay}
              href={`tel:${BUSINESS.phoneDial}`}
              testId="contact-phone"
            />
            <ContactBlock
              icon={MapPin}
              label="Address"
              value={`${BUSINESS.address1}, ${BUSINESS.city}, ${BUSINESS.state} ${BUSINESS.zip}`}
              href={BUSINESS.mapsLink}
              testId="contact-address"
            />
            <ContactBlock
              icon={Mail}
              label="Email"
              value={BUSINESS.email}
              href={`mailto:${BUSINESS.email}`}
              testId="contact-email"
            />

            <div
              data-testid="contact-hours-block"
              className="border border-white/15 bg-white/[0.03] p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#D32F2F] flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="font-mono-label text-[#D32F2F]">Shop Hours</div>
              </div>
              <ul className="divide-y divide-white/10">
                {BUSINESS.hours.map((h) => (
                  <li
                    key={h.day}
                    className="flex items-center justify-between py-2 text-sm"
                  >
                    <span className="text-white/70">{h.day}</span>
                    <span
                      className={`font-semibold ${
                        h.range === "Closed" ? "text-[#D32F2F]" : "text-white"
                      }`}
                    >
                      {h.range}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="appointment-form"
            className="lg:col-span-3 bg-white text-[#0B132B] p-6 md:p-10 border-l-4 border-[#D32F2F]"
          >
            <div className="font-mono-label text-[#D32F2F] mb-2">
              Schedule Service
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mb-8">
              Request Your Appointment
            </h3>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name *" htmlFor="f-name">
                <Input
                  id="f-name"
                  data-testid="form-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jane Doe"
                  required
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F] h-12"
                />
              </Field>
              <Field label="Phone Number *" htmlFor="f-phone">
                <Input
                  id="f-phone"
                  data-testid="form-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="(248) 555-0199"
                  required
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F] h-12"
                />
              </Field>
              <Field label="Email (Optional)" htmlFor="f-email">
                <Input
                  id="f-email"
                  data-testid="form-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F] h-12"
                />
              </Field>
              <Field label="Preferred Date" htmlFor="f-date">
                <Input
                  id="f-date"
                  data-testid="form-date-input"
                  type="date"
                  value={form.preferred_date}
                  onChange={(e) => update("preferred_date", e.target.value)}
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F] h-12"
                />
              </Field>
              <Field label="Vehicle (Year / Make / Model) *" htmlFor="f-vehicle">
                <Input
                  id="f-vehicle"
                  data-testid="form-vehicle-input"
                  value={form.vehicle}
                  onChange={(e) => update("vehicle", e.target.value)}
                  placeholder="2019 Ford F-150"
                  required
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F] h-12"
                />
              </Field>
              <Field label="Service Needed *" htmlFor="f-service">
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger
                    id="f-service"
                    data-testid="form-service-select"
                    className="rounded-none border-[#E2E8F0] focus:ring-[#D32F2F] h-12"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.key} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other">Other / Not Sure</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Describe the Issue" htmlFor="f-message">
                <Textarea
                  id="f-message"
                  data-testid="form-message-input"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Describe the symptoms, noises, or service requested..."
                  rows={4}
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F]"
                />
              </Field>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#D32F2F] hover:bg-[#B71C1C] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-4 px-8 rounded-none transition-colors"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" strokeWidth={2.5} />
                )}
                {loading ? "Sending..." : "Request Appointment"}
              </button>
              <a
                href={`tel:${BUSINESS.phoneDial}`}
                data-testid="form-call-fallback-btn"
                className="inline-flex items-center justify-center gap-2 text-[#0B132B] hover:text-[#D32F2F] font-semibold text-sm"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                Prefer to call? {BUSINESS.phoneDisplay}
              </a>
            </div>

            <p className="mt-5 text-xs text-[#4A5568]">
              We'll confirm your slot within one business day. By submitting
              you consent to be contacted about your request. We never share
              your info.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="font-mono-label text-[#0B132B] mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ContactBlock({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <>
      <div className="w-10 h-10 bg-[#D32F2F] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <div className="font-mono-label text-[#D32F2F] mb-1">{label}</div>
        <div className="font-display text-lg font-semibold text-white break-words">
          {value}
        </div>
      </div>
    </>
  );
  return href ? (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className="flex gap-4 items-start border border-white/15 bg-white/[0.03] p-5 hover:bg-white/[0.06] transition-colors"
    >
      {inner}
    </a>
  ) : (
    <div className="flex gap-4 items-start border border-white/15 bg-white/[0.03] p-5">
      {inner}
    </div>
  );
}
